/**
 * useMaxExploreCatalog hook
 *
 * Reactive bridge between the Explore empty state UI and the catalog store.
 * Triggers catalog load when tenantId is available; exposes loading/error/catalog
 * state and local selectedCategory filter.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { IExploreCatalogService } from '../core/services/exploreCatalogService'
import { ExploreCatalogCard, ExploreCatalogCategory } from '../core/types/explore.types'
import useMaxExploreCatalogStore from '../stores/use-max-explore-catalog-store'

type UseMaxExploreCatalogOptions = {
  tenantId: string | null | undefined
  service: IExploreCatalogService | null | undefined
}

type UseMaxExploreCatalogReturn = {
  isLoading: boolean
  error: string | null
  categories: ExploreCatalogCategory[]
  cards: ExploreCatalogCard[]
  filteredCards: ExploreCatalogCard[]
  selectedCategory: string
  setCategory: (categoryId: string) => void
  reload: () => void
}

const useMaxExploreCatalog = ({
  tenantId,
  service,
}: UseMaxExploreCatalogOptions): UseMaxExploreCatalogReturn => {
  const isLoading = useMaxExploreCatalogStore((state) => state.isLoading)
  const error = useMaxExploreCatalogStore((state) => state.error)
  const getCatalog = useMaxExploreCatalogStore((state) => state.getCatalog)
  const loadCatalog = useMaxExploreCatalogStore((state) => state.loadCatalog)
  const clearCatalog = useMaxExploreCatalogStore((state) => state.clearCatalog)

  const [selectedCategory, setSelectedCategory] = useState('all')

  // Track catalog entry reactively
  const catalogByTenant = useMaxExploreCatalogStore((state) => state.catalogByTenant)
  const catalog = tenantId ? getCatalog(tenantId) : null

  const categories = useMemo<ExploreCatalogCategory[]>(
    () => catalog?.categories ?? [],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [catalog, catalogByTenant]
  )

  const cards = useMemo<ExploreCatalogCard[]>(
    () => catalog?.cards ?? [],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [catalog, catalogByTenant]
  )

  const filteredCards = useMemo<ExploreCatalogCard[]>(() => {
    if (selectedCategory === 'all') return cards
    return cards.filter((c) => c.categoryId === selectedCategory)
  }, [cards, selectedCategory])

  const serviceRef = useRef(service)
  serviceRef.current = service

  const loadForTenant = useCallback(
    (id: string) => {
      const svc = serviceRef.current
      if (!svc) return
      void loadCatalog(id, svc)
    },
    [loadCatalog]
  )

  useEffect(() => {
    if (tenantId) loadForTenant(tenantId)
  }, [tenantId, loadForTenant])

  const reload = useCallback(() => {
    if (!tenantId || !serviceRef.current) return
    clearCatalog(tenantId)
    loadForTenant(tenantId)
  }, [tenantId, clearCatalog, loadForTenant])

  const setCategory = useCallback((categoryId: string) => {
    setSelectedCategory(categoryId)
  }, [])

  return {
    isLoading,
    error,
    categories,
    cards,
    filteredCards,
    selectedCategory,
    setCategory,
    reload,
  }
}

export default useMaxExploreCatalog
